import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaArrowLeft, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import { NEWS, NewsItem, formatNewsDate, newsImageUrl, newsKicker, newsText } from '../data/news';
import './NewsPage.css';

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay },
  viewport: { once: true },
});

/** Lista vesti na /vesti/ */
export const NewsList: React.FC = () => {
  const { t, language } = useLanguage();

  return (
    <div className="App App--cup news-page">
      <Header />
      <main>
        <section className="news-hero">
          <div className="container">
            <p className="news-hero__kicker">{t('news.kicker')}</p>
            <h1 className="news-hero__title">{t('news.title')}</h1>
            <p className="news-hero__lead">{t('news.lead')}</p>
          </div>
        </section>

        <section className="news-list-section">
          <div className="container news-list">
            {NEWS.map((item, i) => {
              const text = newsText(item, language);
              return (
                <motion.article key={item.slug} className="news-card" {...reveal(i * 0.06)}>
                  <a className="news-card__link" href={`/vesti/${item.slug}/`}>
                    <div className="news-card__media">
                      <img
                        src={newsImageUrl(item, item.images[0], true)}
                        alt={item.images[0].alt}
                        loading={i === 0 ? 'eager' : 'lazy'}
                        width={640}
                        height={427}
                      />
                    </div>
                    <div className="news-card__body">
                      <span className="news-card__date">{formatNewsDate(item.date, language)}</span>
                      <h2 className="news-card__title">{text.title}</h2>
                      <p className="news-card__lead">{text.lead}</p>
                      <span className="news-card__more">
                        {t('news.readMore')} <FaArrowRight aria-hidden />
                      </span>
                    </div>
                  </a>
                </motion.article>
              );
            })}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

interface ArticleProps {
  item: NewsItem;
}

/** Pojedinačna vest na /vesti/<slug>/ */
export const NewsArticle: React.FC<ArticleProps> = ({ item }) => {
  const { t, language } = useLanguage();
  const text = newsText(item, language);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const close = useCallback(() => setLightbox(null), []);
  const prev = useCallback(
    () => setLightbox((i) => (i === null ? null : (i - 1 + item.images.length) % item.images.length)),
    [item.images.length]
  );
  const next = useCallback(
    () => setLightbox((i) => (i === null ? null : (i + 1) % item.images.length)),
    [item.images.length]
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox, close, prev, next]);

  const gallery = item.images.slice(1);

  return (
    <div className="App App--cup news-page">
      <Header />
      <main>
        <article className="news-article">
          <div className="container news-article__head">
            <a className="news-article__back" href="/vesti/">
              <FaArrowLeft aria-hidden /> {t('news.backToList')}
            </a>
            <p className="news-article__kicker">{newsKicker(item, language)}</p>
            <h1 className="news-article__title">{text.title}</h1>
            <p className="news-article__date">
              <time dateTime={item.date}>{formatNewsDate(item.date, language)}</time>
            </p>
          </div>

          <div className="container news-article__cover">
            <img
              src={newsImageUrl(item, item.images[0])}
              alt={item.images[0].alt}
              width={1440}
              height={960}
              fetchPriority="high"
            />
          </div>

          <div className="container news-article__body">
            <p className="news-article__lead">{text.lead}</p>
            {text.paragraphs.map((p) => (
              <p key={p.slice(0, 36)}>{p}</p>
            ))}

            {item.videoId && (
              <div className="news-article__video">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${item.videoId}`}
                  title={text.title}
                  loading="lazy"
                  allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            )}
          </div>

          {gallery.length > 0 && (
            <div className="container news-article__gallery">
              <h2 className="news-article__gallery-title">{t('news.gallery')}</h2>
              <div className="news-article__grid">
                {gallery.map((img, i) => (
                  <button
                    type="button"
                    key={img.file}
                    className="news-article__thumb"
                    onClick={() => setLightbox(i + 1)}
                    aria-label={img.alt}
                  >
                    <img src={newsImageUrl(item, img)} alt={img.alt} loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="container news-article__cta">
            <h2>{t('news.ctaTitle')}</h2>
            <p>{t('news.ctaText')}</p>
            <div className="news-article__cta-actions">
              <a className="news-btn news-btn--primary" href="/upis/">{t('footer.links.privateCoaching')}</a>
              <a className="news-btn" href="/raspored/">{t('parents.schedule.title')}</a>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {lightbox !== null && (
        <div className="news-lightbox" role="dialog" aria-modal onClick={close}>
          <button type="button" className="news-lightbox__close" onClick={close} aria-label={t('news.close')}>
            <FaTimes />
          </button>
          <button
            type="button"
            className="news-lightbox__nav news-lightbox__nav--prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label={t('news.prev')}
          >
            <FaChevronLeft />
          </button>
          <img
            className="news-lightbox__img"
            src={newsImageUrl(item, item.images[lightbox])}
            alt={item.images[lightbox].alt}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="news-lightbox__nav news-lightbox__nav--next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label={t('news.next')}
          >
            <FaChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

/** Kratak pregled najnovijih vesti na početnoj strani. */
export const NewsTeaser: React.FC = () => {
  const { t, language } = useLanguage();
  const items = NEWS.slice(0, 3);
  if (items.length === 0) return null;

  return (
    <section id="vesti" className="news-teaser section-lg" aria-labelledby="news-teaser-title">
      <div className="container">
        <motion.div className="news-teaser__header" {...reveal()}>
          <span className="news-teaser__mark" aria-hidden />
          <h2 id="news-teaser-title" className="news-teaser__title">{t('news.title')}</h2>
        </motion.div>

        <div className="news-teaser__grid">
          {items.map((item, i) => {
            const text = newsText(item, language);
            return (
              <motion.a key={item.slug} className="news-teaser__card" href={`/vesti/${item.slug}/`} {...reveal(0.07 * i)}>
                <img
                  src={newsImageUrl(item, item.images[0], true)}
                  alt={item.images[0].alt}
                  loading="lazy"
                  width={640}
                  height={427}
                />
                <span className="news-teaser__date">{formatNewsDate(item.date, language)}</span>
                <h3 className="news-teaser__card-title">{text.title}</h3>
                <span className="news-teaser__more">{t('news.readMore')} <FaArrowRight aria-hidden /></span>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
